import { PaymentPersistence } from "core/persistence";
import { PaymentService } from "core/service";
import BaseRepository from "core/repository/base.repository";
import { Payment } from "core/entity";
import { AcquirerNames, NetworkNames, SaleStatus } from "core/enum";
import Moment from "moment";
import Papa from "papaparse";
import Big from "big.js";

class PaymentRepository extends BaseRepository {
  syncable = false;

  getEntityIdentifier() {
    return "payment";
  }

  resourceName() {
    return "pagamentos";
  }

  getPersistenceProvider() {
    return PaymentPersistence;
  }

  getServiceProvider() {
    return PaymentService;
  }

  async modelToEntity(model) {
    return new Payment(model);
  }

  async getSummary(filters, customOtherPayments) {
    const baseSummary = {};
    const allOtherPayments = [];
    const onlyUuidsOfOtherPayments = customOtherPayments
      .map(payment => payment.uuid)
      .join("|");

    // TODO: Remove company_uuid from fetch
    const currentUser = await this.mRuntime.get("currentUser");
    const companyUuid = currentUser.company_uuid;

    // Payments without installments grouped by type
    const withoutInstallmentsPromise = this.mService.fetch(companyUuid, {
      filters: [
        ...filters,
        {
          connector: "AND",
          field: "metadata.installments",
          operation: "EQ",
          value: 1
        },
        {
          connector: "AND",
          field: "metadata.other_payment_uuid",
          operation: "NIN",
          value: onlyUuidsOfOtherPayments
        }
      ],
      group: {
        field: "type",
        transformations: [
          { field: "type", operation: "LAST" },
          { field: "amount", operation: "SUM" },
          { field: "metadata.cashback", operation: "SUM" }
        ]
      }
    });

    // Payments with installments grouped by type (No cashback expected)
    const withInstallmentsPromise = this.mService.fetch(companyUuid, {
      filters: [
        ...filters,
        {
          connector: "AND",
          field: "metadata.installments",
          operation: "GT",
          value: 1
        },
        {
          connector: "AND",
          field: "metadata.other_payment_uuid",
          operation: "NIN",
          value: onlyUuidsOfOtherPayments
        }
      ],
      group: {
        field: "type",
        transformations: [
          { field: "type", operation: "LAST" },
          { field: "amount", operation: "SUM" }
        ]
      }
    });

    if (customOtherPayments.length !== 0) {
      for (const other of customOtherPayments) {
        const otherPayments = await this.mService.fetch(companyUuid, {
          filters: [
            ...filters,
            {
              connector: "AND",
              field: "metadata.other_payment_uuid",
              operation: "EQ",
              value: other.uuid
            }
          ],
          group: {
            field: "type",
            transformations: [
              { field: "amount", operation: "SUM" },
              { field: "metadata.other_payment_uuid", operation: "LAST" }
            ]
          }
        });

        allOtherPayments.push(otherPayments);
      }
    }

    // Waits requisitions
    const [withoutInstallments, withInstallments] = await Promise.all([
      withoutInstallmentsPromise,
      withInstallmentsPromise
    ]);

    // Returns if someone had insuccess

    if (!withoutInstallments.success) {
      return withoutInstallments;
    }

    if (!withInstallments.success) {
      return withInstallments;
    }

    //Builds summary
    for (const payment of allOtherPayments) {
      const paymentAmount = payment.data.reduce(
        (accumulator, payment_data) => {
          return {
            amount: accumulator.amount + payment_data.amount,
            metadata_other_payment_uuid:
              payment_data.metadata_other_payment_uuid
          };
        },
        { amount: 0, metadata_other_payment_uuid: "" }
      );

      baseSummary[paymentAmount.metadata_other_payment_uuid] =
        paymentAmount.amount;
    }

    withoutInstallments.data.forEach(
      ({ amount, type, metadata_cashback }) =>
        (baseSummary[type] = amount - (metadata_cashback || 0))
    );
    withInstallments.data.forEach(
      ({ amount, type }) => (baseSummary.installments = amount)
    );
    const summary = Object.entries(baseSummary).map(([key, value]) => ({
      type: key,
      amount: value
    }));

    return { success: true, data: summary };
  }

  async sendEmailReport({ email, filters, order }) {
    const currentUser = await this.mRuntime.get("currentUser");
    const companyUuid = currentUser.company_uuid;

    return await this.mService.sendEmailReport(companyUuid, {
      email,
      filters,
      order: order.filter(param => !param.mapFunction)
    });
  }

  async getCsvContent({ order, filters }) {
    const fields = [
      "Data",
      "Total (R$)",
      "Venda",
      "Status da Venda",
      "Meio de Pagamento",
      "Parcelas",
      "Bandeira",
      "Credenciadora",
      "NSU"
    ];
    const data = [];

    // TODO: Remove company_uuid from stream
    const currentUser = await this.mRuntime.get("currentUser");
    const companyUuid = currentUser.company_uuid;

    const success = await this.mService.stream(
      companyUuid,
      { order, filters },
      async model => {
        const payment = await this.modelToEntity(model);

        const sale = payment.sale;

        data.push([
          Moment(payment.processed_at).format("DD/MM/YYYY HH:mm:ss"),
          Big(payment.getRealAmount())
            .div(100)
            .toFixed(2),
          sale && `${sale.type} n.: ${sale.number}`,
          sale && SaleStatus[sale.status],
          payment.friendlyType(),
          payment.metadata && payment.metadata.installments,
          payment.metadata && NetworkNames[payment.metadata.network],
          payment.metadata && AcquirerNames[payment.metadata.acquirer_name],
          payment.metadata && payment.metadata.acquirer_authorization_number
        ]);
      }
    );

    if (!success) {
      return { success: false };
    }

    return {
      success: true,
      data: Papa.unparse({ fields, data })
    };
  }
}

export default new PaymentRepository();



// WEBPACK FOOTER //
// ./src/core/repository/payment.repository.js